import { List } from 'immutable';
import { NodeType, ParseOptions, Schema } from 'hubspot-prosemirror/pm/model';
import { EditorState, Plugin } from 'hubspot-prosemirror/pm/state';
import { FocusPlugin } from './plugins/FocusPlugin';
import { findAllNodes, findNode } from './utils/nodeUtils';
import { SuggestionItem } from '../types';
import { getDomParser } from './utils/domparser';
import {
  PlaintextOptions,
  convertToPlaintextState,
} from './utils/plaintextUtils';
import { buildDefaultRTEConfig } from './utils/configUtils';
import { EditorConfig } from './editorCapabilities/editorCapabilitiesTypes';
import { EditorStateSerializer } from './utils/EditorStateSerializer';

/**
 * Convert the editor state to an HTML string.
 *
 * This function uses the `toDOM` method of each schema node to produce HTML.
 *
 * This function will remove any nodes with the `data-pm-output="none"` attribute
 * before returning the HTML string.
 *
 * If you wish to exclude a node from the HTML output, you can add the
 * `data-pm-output` attribute to its `toDOM` output specification.
 *
 * For advanced use cases that require custom node serialization (e.g.
 * substituting source HTML for specific nodes), construct an
 * {@link EditorStateSerializer} directly and call `addNodeModifier` before
 * `toHTML`.
 */
export function convertToHtml(editorState: EditorState) {
  return new EditorStateSerializer(editorState).toHTML();
}

export function convertToPlaintext(
  editorState: EditorState,
  options?: PlaintextOptions
) {
  const plainState = convertToPlaintextState(editorState, options);
  return plainState.doc.textBetween(0, plainState.doc.content.size, '\n');
}

export function hasContent(editorState: EditorState) {
  // checks for content fragment size (minus two for boundaries of Node)
  return !!(editorState.doc.content.size - 2);
}

export function isUploadingImages(editorState: EditorState) {
  return !!findNode(
    editorState.doc,
    (n) => n.type === editorState.schema.nodes.image && n.attrs.isTemporary
  );
}

export function convertToJSON(editorState: EditorState) {
  return editorState.toJSON();
}

export function getEditorStateFromJSON(
  json: { [key: string]: any },
  customPlugins?: Array<Plugin>
) {
  return EditorState.fromJSON(buildDefaultRTEConfig(customPlugins), json);
}

export function getEditorStateFromHtml(
  htmlContent: string,
  customPlugins?: Plugin[],
  options?: Parameters<typeof getDocNodeFromHtml>[1],
  schema?: Schema,
  editorConfig?: EditorConfig
) {
  const docNode = getDocNodeFromHtml(htmlContent, options, schema);
  return EditorState.create({
    doc: docNode,
    ...buildDefaultRTEConfig(customPlugins, editorConfig),
  });
}

const BLOCK_CHILD_PARENTS = new Set([
  'UL',
  'OL',
  'LI',
  'TABLE',
  'TBODY',
  'THEAD',
  'TFOOT',
  'TR',
  'DL',
]);

function normalizeWhitespace(node: Node) {
  if (node.nodeType === Node.TEXT_NODE) {
    const parent = node.parentNode;
    if (parent) {
      // Inside elements that only accept block children (lists, tables),
      // whitespace-only text nodes are insignificant formatting.
      // Converting their newlines to <br> injects phantom empty list
      // items when parsing third-party HTML.
      if (
        BLOCK_CHILD_PARENTS.has(parent.nodeName) &&
        node.textContent != null &&
        node.textContent.trim() === ''
      ) {
        return;
      }
      const textParts = node?.textContent?.split('\n') || [];
      if (textParts.length > 0 && parent) {
        for (let index = 0; index < textParts.length; index++) {
          const textNode = document.createTextNode(textParts[index]);
          parent.insertBefore(textNode, node);

          if (index < textParts.length - 1) {
            parent.insertBefore(
              document.createElement('br'),
              textNode?.nextSibling
            );
          }
        }
        parent.removeChild(node);
      }
    }
  }
  const whiteSpaceStyle = (node as HTMLElement)?.style?.whiteSpace;
  const nodeRespectsNewLines =
    ['pre', 'pre-wrap', 'nowrap'].includes(whiteSpaceStyle) ||
    node.nodeName === 'PRE';
  const isSignature =
    node.nodeName === 'DIV' &&
    !!(node as HTMLDivElement).getAttribute('data-hs-signature');
  const parseAsMark = node.nodeName === 'A'; // Marks that should not have hard breaks normalized
  const shouldNormalizeWhitespaceInChildren =
    !nodeRespectsNewLines && !isSignature && !parseAsMark;
  if (shouldNormalizeWhitespaceInChildren) {
    let targetNode: ChildNode | null = node.childNodes[0];
    while (targetNode) {
      const nextSibling = targetNode.nextSibling as ChildNode | null;
      normalizeWhitespace(targetNode);
      targetNode = nextSibling;
    }
  }
}

export function getDocNodeFromHtml(
  htmlContent: string,
  options: {
    preserveNewlines?: boolean;
    preserveWhitespace?: boolean | 'full';
  } = {},
  schema?: Schema
) {
  const defaultOptions = {
    preserveNewlines: true,
  };
  const mergedOptions = { ...defaultOptions, ...options };
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  if (mergedOptions.preserveNewlines) {
    normalizeWhitespace(doc.body);
  }

  // Pass preserveWhitespace option to ProseMirror parser
  const parseOptions: ParseOptions = {};
  if (mergedOptions.preserveWhitespace !== undefined) {
    parseOptions.preserveWhitespace = mergedOptions.preserveWhitespace;
  }

  return getDomParser(schema).parse(doc.body, parseOptions);
}

export function focusEditor(editorState: EditorState, focusViewPos?: number) {
  return editorState.apply(
    editorState.tr.setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos,
    })
  );
}

export function getAtMentions(editorState: EditorState) {
  const mentions = findAllNodes(
    editorState.doc,
    (node) => node.type === editorState.schema.nodes.atmention
  );
  let mentionIds = List<number>();
  mentions.forEach((mention) => {
    const ownerId = parseInt(mention.node.attrs.id, 10);
    if (ownerId && !isNaN(ownerId) && !mentionIds.has(ownerId)) {
      mentionIds = mentionIds.push(ownerId);
    }
  });
  return mentionIds;
}

export interface MissingSnippetToken {
  tokenKey: string;
  displayText: string | null;
  pos: number;
}

// Collects the personalization tokens currently flagged as unresolved
// (error=true) in the document, in document order. Used by consumers that
// render a prompt when inserted snippet tokens have no value. Derives purely
// from the doc so it recomputes naturally on every change.
export function getMissingSnippetTokens(
  editorState: EditorState
): MissingSnippetToken[] {
  const nodesFound = findAllNodes(
    editorState.doc,
    (node) =>
      node.type === editorState.schema.nodes.personalization_token &&
      Boolean(node.attrs.error)
  );
  return nodesFound.map(({ node, pos }) => ({
    tokenKey: node.attrs.tokenKey,
    displayText: node.attrs.displayText ?? null,
    pos,
  }));
}

// Removes the unresolved (error=true) snippet personalization tokens from the
// document. Used before serializing on save so a blank snippet token is stored
// as empty — matching the default blank-on-missing behavior — rather than
// persisting the error chip markup. A no-op when there are no error tokens.
export function stripErrorSnippetTokens(editorState: EditorState): EditorState {
  const errorNodes = findAllNodes(
    editorState.doc,
    (node) =>
      node.type === editorState.schema.nodes.personalization_token &&
      Boolean(node.attrs.error)
  );
  if (errorNodes.length === 0) {
    return editorState;
  }
  let transaction = editorState.tr;
  [...errorNodes]
    .sort((first, second) => second.pos - first.pos)
    .forEach(({ pos }) => {
      transaction = transaction.delete(pos, pos + 1);
    });
  return editorState.apply(transaction);
}

export function getNumberOfNodeType(
  editorState: EditorState,
  nodeType: NodeType
) {
  const nodesFound = findAllNodes(
    editorState.doc,
    (node) => node.type === nodeType
  );
  return nodesFound.length;
}

export function getNumberOfImages(editorState: EditorState) {
  return getNumberOfNodeType(editorState, editorState.schema.nodes.image);
}

export function getNumberOfLinks(editorState: EditorState) {
  let numLinks = 0;
  editorState.doc.descendants((node) => {
    if (editorState.schema.marks.link.isInSet(node.marks)) {
      numLinks++;
    }
    return true;
  });
  return numLinks;
}

export type GetMatchResultsOptions = {
  sortAlphabetically?: boolean;
};

function sortByName(items: SuggestionItem[]): SuggestionItem[] {
  return [...items].sort((a, b) => {
    const nameA = (a.name || a.searchText || '').toLowerCase();
    const nameB = (b.name || b.searchText || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

/**
 * Filters and optionally sorts suggestion items based on a search query.
 *
 * Results are categorized into two groups:
 * 1. "Starts with" matches - items where the name/searchText begins with the query
 * 2. "Contains" matches - items where the query appears elsewhere in the name/searchText
 *
 * "Starts with" matches are always returned before "contains" matches.
 *
 * @param query - The search string to match against
 * @param data - Array of suggestion items to filter
 * @param options.sortAlphabetically - When true, sorts each group alphabetically by name.
 *   When false or omitted, preserves insertion order within each group.
 */
export function getMatchResults(
  query: string,
  data: SuggestionItem[],
  options?: GetMatchResultsOptions
) {
  const exactRegex = new RegExp(
    `^${query.trim().replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}`,
    'i'
  );
  const globalRegex = new RegExp(
    query.trim().replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'),
    'i'
  );

  const matchResults = data.reduce(
    (acc, item: SuggestionItem) => {
      const toSearch = item.searchText || item.name || '';
      const isExactMatch = toSearch.match(exactRegex) !== null;
      if (isExactMatch) {
        return {
          ...acc,
          exactMatches: [...acc.exactMatches, item],
        };
      }
      const isGlobalMatch = toSearch.match(globalRegex) !== null;
      if (isGlobalMatch) {
        return {
          ...acc,
          globalMatches: [...acc.globalMatches, item],
        };
      }
      return acc;
    },
    {
      exactMatches: [] as SuggestionItem[],
      globalMatches: [] as SuggestionItem[],
    }
  );

  const exactMatches = options?.sortAlphabetically
    ? sortByName(matchResults.exactMatches)
    : matchResults.exactMatches;

  const globalMatches = options?.sortAlphabetically
    ? sortByName(matchResults.globalMatches)
    : matchResults.globalMatches;

  return [...exactMatches, ...globalMatches];
}
